import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldWarning, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";

/**
 * Firm-wide "Active PEP/Sanctions matches" panel.
 *
 * Only shows aml_checks whose watchlist_summary.total > 0 AND whose
 * parent case is still active. Data comes from SmartSearch and is
 * cached on the check whenever a solicitor opens the case-detail
 * Watchlist panel — so older checks never reviewed won't appear
 * (that's deliberate — analytics == reviewed-and-open matters).
 *
 * Compact: designed to slot below the main audit table without
 * doubling page height.
 */
export function PepMatchesPanel() {
  const [state, setState] = useState({
    loading: true, rows: [], totals: {}, error: null,
  });

  const fetchData = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await API.get("/aml/analytics/pep-matches");
      setState({
        loading: false, rows: data.rows || [],
        totals: data.totals || {}, error: null,
      });
    } catch (err) {
      setState((s) => ({
        ...s, loading: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || "Failed to load PEP matches",
      }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Hide when the firm has zero flagged clients — no signal, no noise.
  if (!state.loading && state.rows.length === 0 && !state.error) {
    return null;
  }

  const t = state.totals;

  return (
    <div
      className="mt-6 bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="pep-matches-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-rose-50/40">
        <div className="flex items-center gap-2">
          <ShieldWarning weight="fill" className="w-4 h-4 text-rose-600" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Active watchlist matches
              <span className="ml-2 text-xs font-normal text-slate-500">
                Clients on PEP / Sanctions / RCA / SIP lists
              </span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              PEP {t.pep || 0} · Sanctions {t.sanctions || 0} · RCA {t.rca || 0} · SIP {t.sip || 0} — across {t.rows || 0} client{t.rows === 1 ? "" : "s"}.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={state.loading}
          className="h-8 px-3 text-xs"
          data-testid="pep-matches-refresh"
        >
          <ArrowsClockwise weight="bold" className={`w-3 h-3 mr-1.5 ${state.loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {state.error && (
        <p className="px-4 py-3 text-xs text-rose-600">{state.error}</p>
      )}

      {state.loading ? (
        <p className="px-4 py-4 text-xs text-slate-400 text-center">Loading…</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="pep-matches-list">
          {state.rows.map((r) => (
            <li key={r.check_id} data-testid={`pep-match-${r.check_id}`}>
              <Link
                to={`/cases/${r.case_id}?tab=aml`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 no-underline text-slate-900"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {r.client_name || "—"}
                    {r.case_ref && (
                      <span className="ml-2 font-mono text-[11px] text-slate-500">{r.case_ref}</span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {r.case_title}
                    {r.matches?.length > 0 && (
                      <span className="ml-2 text-slate-400">
                        · {r.matches.slice(0, 2).map((m) => m.name).filter(Boolean).join(" · ")}
                        {r.matches.length > 2 && ` + ${r.matches.length - 2}`}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.pep > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-rose-100 text-rose-700 border border-rose-200">
                      PEP {r.pep}
                    </span>
                  )}
                  {r.sanctions > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-red-100 text-red-800 border border-red-200">
                      SAN {r.sanctions}
                    </span>
                  )}
                  {r.rca > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 border border-amber-200">
                      RCA {r.rca}
                    </span>
                  )}
                  {r.sip > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-slate-100 text-slate-700 border border-slate-200">
                      SIP {r.sip}
                    </span>
                  )}
                </div>
                <ArrowRight weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PepMatchesPanel;
